import React, { useEffect, useState } from 'react';
import { Button, Flex, Form, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CustomButton, CustomInput, CustomShipmentText } from '@/components';
import { DeleteIcon } from '@/assets/icons/icons';
import { CustomDeleteModal } from '@/pages/Modals';
import './TagList.css';
import type {
  AlertSettingsFormValues,
  AlertTagValue,
  TagListName,
} from '../alertSettingsForm';

export const MAX_TAGS_PER_LIST = 12;
export const MIN_TAG_LENGTH = 2;
export const MAX_TAG_LENGTH = 40;
// Letters and numbers in any language, plus spaces and a few common marks.
const ALLOWED_TAG_CHARACTERS = /^[\p{L}\p{N} \-_&'’.,()]+$/u;
const STARTS_WITH_LETTER_OR_NUMBER = /^[\p{L}\p{N}]/u;
const MARKS_IN_A_ROW = /[-_&'’.,()]{2,}/u;
export const INVALID_TAG_CHARACTERS_MESSAGE =
  "Use only letters, numbers, spaces and - _ & ' . , ( )";
export const TAG_MUST_START_WITH_LETTER_OR_NUMBER_MESSAGE =
  'Start with a letter or number';
export const TAG_MARKS_IN_A_ROW_MESSAGE =
  'Do not put punctuation marks next to each other';

export interface TagListProps {
  name: TagListName;
  deleteLabel: string;
  /** The list needs at least one tag (its "Require a tag" toggle is on). */
  required?: boolean;
  /** Shown when a required list is empty; should say why and how to fix it. */
  requiredMessage?: string;
}

const TagList: React.FC<TagListProps> = ({
  name,
  deleteLabel,
  required = false,
  requiredMessage = 'Add at least one tag.',
}) => {
  const form = Form.useFormInstance<AlertSettingsFormValues>();
  // The saved tag awaiting delete confirmation, with its row index.
  const [pendingDelete, setPendingDelete] = useState<{
    index: number;
    tag: AlertTagValue;
  } | null>(null);

  const requestRemove = (index: number, remove: (index: number) => void) => {
    const tag: AlertTagValue | undefined = form.getFieldValue([name, index]);
    // A tag that is unsaved or on no alert can go straight away; only a tag
    // that is in use needs a confirmation.
    if (!tag?.id || !tag.used_count) {
      remove(index);
      return;
    }
    setPendingDelete({ index, tag });
  };

  const validateLabel = (_: unknown, value?: string): Promise<void> => {
    const label = (value ?? '').trim();
    if (label.length < MIN_TAG_LENGTH || label.length > MAX_TAG_LENGTH) {
      return Promise.reject(
        new Error(`Use ${MIN_TAG_LENGTH}–${MAX_TAG_LENGTH} characters`)
      );
    }
    if (!ALLOWED_TAG_CHARACTERS.test(label)) {
      return Promise.reject(new Error(INVALID_TAG_CHARACTERS_MESSAGE));
    }
    if (!STARTS_WITH_LETTER_OR_NUMBER.test(label)) {
      return Promise.reject(
        new Error(TAG_MUST_START_WITH_LETTER_OR_NUMBER_MESSAGE)
      );
    }
    if (MARKS_IN_A_ROW.test(label)) {
      return Promise.reject(new Error(TAG_MARKS_IN_A_ROW_MESSAGE));
    }
    const tags: AlertTagValue[] = form.getFieldValue(name) ?? [];
    const matches = tags.filter(
      (tag) => (tag?.label ?? '').trim().toLowerCase() === label.toLowerCase()
    );
    return matches.length > 1
      ? Promise.reject(new Error('Tag names must be unique'))
      : Promise.resolve();
  };

  const validateNotEmpty = (_: unknown, list?: unknown[]): Promise<void> =>
    list?.length
      ? Promise.resolve()
      : Promise.reject(new Error(requiredMessage));

  // An empty list is fine while no tag is required; drop a stale error once
  // the requirement is switched off.
  useEffect(() => {
    if (!required) form.setFields([{ name, errors: [] }]);
  }, [required, form, name]);

  return (
    <>
      <Form.List
        name={name}
        rules={required ? [{ validator: validateNotEmpty }] : []}
      >
        {(fields, { add, remove }, { errors }) => (
          <Flex vertical gap={4} style={{ maxWidth: 480 }}>
            <CustomDeleteModal
              visible={pendingDelete !== null}
              onClose={() => setPendingDelete(null)}
              onConfirm={() => {
                if (pendingDelete) remove(pendingDelete.index);
                setPendingDelete(null);
              }}
              title='Are you sure?'
              message={`Used on ${pendingDelete?.tag?.used_count} ${
                pendingDelete?.tag?.used_count === 1 ? 'alert' : 'alerts'
              }. Historical entries keep the tag. It will no longer be offered when closing or assigning alerts.`}
              cancelText='Cancel'
              confirmText='Delete'
              confirmColor='var(--danger)'
            />
            <CustomShipmentText
              text={'Tags'}
              fontSize={'14'}
              fontWeight={600}
            />
            {fields.map((field) => (
              <Flex key={field.key} gap={4} align='start'>
                <Form.Item name={[field.name, 'id']} hidden>
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[field.name, 'label']}
                  dependencies={[[name]]}
                  validateTrigger={['onChange', 'onBlur']}
                  style={{ marginBottom: 0, flex: 1 }}
                  rules={[{ validator: validateLabel }]}
                >
                  <CustomInput
                    placeholder='Enter tag name'
                    height='36px'
                    border='0px'
                    bgColor='var(--light-gray)'
                    textColor='var(--text-secondary)'
                    borderRadius='8px'
                    placeholderColor='var(--text-secondary)'
                    lineHeight='20px'
                  />
                </Form.Item>
                <Button
                  aria-label={deleteLabel}
                  icon={<DeleteIcon />}
                  className='border-0 bs-none'
                  onClick={() => requestRemove(field.name, remove)}
                />
              </Flex>
            ))}
            <div className='tag-list__errors'>
              <Form.ErrorList errors={errors} />
            </div>
            <CustomButton
              text='Add'
              onClick={() => add({ label: '' })}
              width='fit-content'
              height='24px'
              bgColor='transparent'
              color='var(--text-primary)'
              fontSize='12px'
              fontWeight={600}
              borderRadius='0px'
              boxShadow='none'
              icon={<PlusOutlined />}
              iconPosition='left'
              gap={'8px'}
              padding={'12px 12px 6px'}
              disabled={fields.length >= MAX_TAGS_PER_LIST}
            />
          </Flex>
        )}
      </Form.List>
    </>
  );
};

export default TagList;
